import React from 'react';
import MobileDetect from 'mobile-detect';
import PerfectScrollbar from 'perfect-scrollbar';
import { styled } from '@mui/material';
import { useForwardRef } from '@fuse/hooks';
import history from '@history';

import 'perfect-scrollbar/css/perfect-scrollbar.css';

type FuseScrollbarsEvents = {
	onScrollY?: (container: HTMLElement) => void;
	onScrollX?: (container: HTMLElement) => void;
	onScrollUp?: (container: HTMLElement) => void;
	onScrollDown?: (container: HTMLElement) => void;
	onScrollLeft?: (container: HTMLElement) => void;
	onScrollRight?: (container: HTMLElement) => void;
	onYReachStart?: (container: HTMLElement) => void;
	onYReachEnd?: (container: HTMLElement) => void;
	onXReachStart?: (container: HTMLElement) => void;
	onXReachEnd?: (container: HTMLElement) => void;
};

interface FuseScrollbarsProps extends FuseScrollbarsEvents {
	className?: string;
	ref?: React.RefObject<HTMLDivElement>;
	enable?: boolean;
	children: React.ReactNode;
	customScrollbars?: boolean;
	option?: PerfectScrollbar.Options;
	scrollToTopOnChildChange?: boolean;
	scrollToTopOnRouteChange?: boolean;
}

const Root = styled('div')(({ theme }) => ({
	// overscrollBehavior: 'contain'
	// If turned on blocks list and details scroll
	// ref: https://www.w3schools.com/cssref/css_pr_overscroll-behavior.php
}));

const md = new MobileDetect(window.navigator.userAgent);
const isMobile = md.mobile();

const handlerNameByEvent = {
	'ps-scroll-y': 'onScrollY',
	'ps-scroll-x': 'onScrollX',
	'ps-scroll-up': 'onScrollUp',
	'ps-scroll-down': 'onScrollDown',
	'ps-scroll-left': 'onScrollLeft',
	'ps-scroll-right': 'onScrollRight',
	'ps-y-reach-start': 'onYReachStart',
	'ps-y-reach-end': 'onYReachEnd',
	'ps-x-reach-start': 'onXReachStart',
	'ps-x-reach-end': 'onXReachEnd'
};
Object.freeze(handlerNameByEvent);

const FuseScrollbars = React.forwardRef<HTMLDivElement, FuseScrollbarsProps>(
	({ className = '', enable = true, option = { wheelPropagation: true }, ...otherProps }, parentRef) => {
		const ref = useForwardRef(parentRef || React.createRef<HTMLDivElement>());
		const ps = React.useRef<PerfectScrollbar | null>(null);
		const handlerByEvent = React.useRef(new Map());

		const hookUpEvents = React.useCallback(() => {
			Object.keys(handlerNameByEvent).forEach(key => {
				const callback = otherProps[handlerNameByEvent[key]];
				if (callback) {
					const handler = () => callback(ref?.current);
					handlerByEvent.current.set(key, handler);
					ref.current.addEventListener(key, handler, false);
				}
			});
			// eslint-disable-next-line
		}, [ref]);

		const unHookUpEvents = React.useCallback(() => {
			handlerByEvent.current.forEach((value, key) => {
				if (ref.current) {
					ref.current.removeEventListener(key, value, false);
				}
			});
			handlerByEvent.current.clear();
		}, [ref]);

		const destroyPs = React.useCallback(() => {
			// console.info("destroy::ps");

			unHookUpEvents();

			if (!ps.current) {
				return;
			}
			ps.current.destroy();
			ps.current = null;
		}, [unHookUpEvents]);

		const createPs = React.useCallback(() => {
			// console.info("create::ps");

			if (isMobile || !ref || ps.current) {
				return;
			}

			ps.current = new PerfectScrollbar(ref.current, option);

			hookUpEvents();
		}, [hookUpEvents, option, ref]);

		React.useEffect(() => {
			function updatePs() {
				if (!ps.current) {
					return;
				}
				ps.current.update();
			}

			updatePs();
		});

		React.useEffect(() => {
			if (otherProps.customScrollbars) {
				createPs();
			} else {
				destroyPs();
			}
		}, [createPs, otherProps.customScrollbars, destroyPs]);

		const scrollToTop = React.useCallback(() => {
			if (ref && ref.current) {
				ref.current.scrollTop = 0;
			}
		}, [ref]);

		React.useEffect(() => {
			if (otherProps.scrollToTopOnChildChange) {
				scrollToTop();
			}
		}, [scrollToTop, otherProps.children, otherProps.scrollToTopOnChildChange]);

		React.useEffect(
			() =>
				history.listen(() => {
					if (otherProps.scrollToTopOnRouteChange) {
						scrollToTop();
					}
				}),
			[scrollToTop, otherProps.scrollToTopOnRouteChange]
		);

		React.useEffect(
			() => () => {
				destroyPs();
			},
			[destroyPs]
		);

		// console.info('render::ps');
		return (
			<Root
				ref={ref}
				className={className}
				style={
					otherProps.customScrollbars && !isMobile
						? {
								position: 'relative',
								overflow: 'hidden!important'
							}
						: {}
				}
			>
				{otherProps.children}
			</Root>
		);
	}
);

export default FuseScrollbars;
